import { MessageCircle, List } from 'lucide-react';

export default function HeroSection() {
  return (
    <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/2d00fbf4-9726-483a-b70b-ea6e421eb3f8/fb17efe3-1e5a-4bfb-acae-896c4b4b3f31_compressed_ChatGPT-Image-21-de-jul.-de-2026-16_58_10.webp"
          alt="Buteco do Baiano - Ambiente acolhedor"
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10 xl:px-16 pt-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Rating badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4].map((s) => (
                <svg key={s} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
              <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <defs><linearGradient id="halfStar"><stop offset="50%" stopColor="currentColor"/><stop offset="50%" stopColor="transparent"/></linearGradient></defs>
                <path fill="url(#halfStar)" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
            <span className="text-white text-sm font-medium">4.4 no Google</span>
            <span className="text-white/60 text-sm">· 577 avaliações</span>
          </div>

          {/* Logo + Brand Name */}
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-6">
            <span className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white whitespace-nowrap">
              Buteco do Baiano
            </span>
          </div>

          {/* Title */}
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 text-balance">
            Comida baiana, drinks e clima de buteco na <span className="text-yellow-400">Varjota</span>
          </h1>

          {/* Subtitle */}
          <p className="text-white/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            O Buteco do Baiano reúne culinária nordestina, petiscos, coquetéis, música ao vivo e aquele clima descontraído para curtir com amigos em Fortaleza.
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/5585987413961?text=Olá!%20Vim%20pelo%20site%20do%20Buteco%20do%20Baiano%20e%20gostaria%20de%20mais%20informações."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-yellow-500 text-white font-semibold rounded-full hover:bg-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              <MessageCircle size={20} />
              Faça sua reserva
            </a>
            <a
              href="https://playmenu.app/public/index.php?r=buteco-do-baiano&from=admin"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-full hover:bg-white/20 transition-all duration-300 whitespace-nowrap"
            >
              <List size={18} />
              Ver cardápio
            </a>
          </div>

          {/* Feature Cards */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            <div className="flex flex-col items-center gap-3 rounded-xl bg-black/40 border border-white/10 backdrop-blur-sm px-4 py-5 text-white">
              <span className="w-8 h-8 flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"/>
                </svg>
              </span>
              <span className="text-sm font-medium">Comida baiana</span>
            </div>
            <div className="flex flex-col items-center gap-3 rounded-xl bg-black/40 border border-white/10 backdrop-blur-sm px-4 py-5 text-white">
              <span className="w-8 h-8 flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18V5l12-2v13"/>
                  <circle cx="6" cy="18" r="3"/>
                  <circle cx="18" cy="16" r="3"/>
                </svg>
              </span>
              <span className="text-sm font-medium">Música ao vivo</span>
            </div>
            <div className="flex flex-col items-center gap-3 rounded-xl bg-black/40 border border-white/10 backdrop-blur-sm px-4 py-5 text-white">
              <span className="w-8 h-8 flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 8h1a4 4 0 010 8h-1"/>
                  <path d="M3 8h14v9a4 4 0 01-4 4H7a4 4 0 01-4-4z"/>
                  <line x1="6" y1="2" x2="6" y2="4"/>
                  <line x1="10" y1="2" x2="10" y2="4"/>
                  <line x1="14" y1="2" x2="14" y2="4"/>
                </svg>
              </span>
              <span className="text-sm font-medium">Drinks</span>
            </div>
            <div className="flex flex-col items-center gap-3 rounded-xl bg-black/40 border border-white/10 backdrop-blur-sm px-4 py-5 text-white">
              <span className="w-8 h-8 flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </span>
              <span className="text-sm font-medium">Varjota</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
          <div className="w-1.5 h-3 bg-white/60 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}